/**
 * Defensive strip of internal prompt markers from LLM-written text before it is shown.
 *
 * The backend fences client data as <untrusted_data>…</untrusted_data> inside LLM prompts and
 * unwraps the fence from every answer (DashboardTextSanitizer). Dashboards saved before that fix,
 * or any path it misses, can still carry the tags, e.g.
 * "The highest respondent count was 743 at <untrusted_data>10.0</untrusted_data>".
 * This keeps the value between the tags and drops the tags (plain, attribute, HTML-escaped and
 * JSON-escaped forms, plus a tag cut off at the end of a truncated text) and the [PII] marker.
 */

const ANY_MARKER = /untrusted_d|\[PII]/i;
const FENCE_TAG = /<\s*\\?\/?\s*untrusted_data\b[^<>]*>/gi;
const ESCAPED_FENCE_TAG = /&lt;\s*\\?\/?\s*untrusted_data\b[^<>&]*(?:&gt;|>)/gi;
const DANGLING_FENCE = /(?:<|&lt;)\s*\\?\/?\s*untrusted_d[a-z_]*$/i;
const PII_MARKER = /\s*\[PII]/g;

/** Keys whose strings are client data or machine payloads, never LLM prose. */
const SKIP_KEYS = new Set(['rows', 'figure_json']);

export const hasLlmMarkers = (text) => typeof text === 'string' && ANY_MARKER.test(text);

/** Unwraps fence tags; text without markers (and non-strings) is returned unchanged. */
export const cleanLlmText = (text) => {
  if (!hasLlmMarkers(text)) return text;
  const out = text
    .replace(FENCE_TAG, '')
    .replace(ESCAPED_FENCE_TAG, '')
    .replace(DANGLING_FENCE, '')
    .replace(PII_MARKER, '');
  if (out === text) return text;
  return out.replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+([.,;:!?)])/g, '$1').trim();
};

/**
 * Deep copy-on-write clean of an API payload: only objects/arrays that contain a changed string
 * are copied; everything else keeps its identity.
 */
export const cleanLlmTextDeep = (value, depth = 0) => {
  if (typeof value === 'string') return cleanLlmText(value);
  if (!value || typeof value !== 'object' || depth > 20) return value;
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((v) => {
      const c = cleanLlmTextDeep(v, depth + 1);
      if (c !== v) changed = true;
      return c;
    });
    return changed ? out : value;
  }
  let out = null;
  Object.keys(value).forEach((k) => {
    if (SKIP_KEYS.has(k)) return;
    const v = value[k];
    const c = cleanLlmTextDeep(v, depth + 1);
    if (c !== v) {
      if (!out) out = { ...value };
      out[k] = c;
    }
  });
  return out || value;
};
